import {
  AppointmentTypeV3EnumDto,
  IFullV3TimelineItemDto,
  SalaryTypeV3EnumDto,
} from '@data-access/bulk-operations-api';

import { LookupItem } from '../pipes/localized-enum-to-lookup-items.pipe';
import {
  EnumLocalizationHelper,
  EnumTypeName,
  LocalizableEnumType,
} from './enum-localization.helper';
import { isTimelineItemActiveAtAnyPointInPeriod } from './timeline.helpers';

type EnumTimelineItem<T extends string> = IFullV3TimelineItemDto & {
  readonly value: T;
};

const salaryTypeTimeline: readonly EnumTimelineItem<SalaryTypeV3EnumDto>[] = [
  { value: SalaryTypeV3EnumDto.Hourly },
  { value: SalaryTypeV3EnumDto.Fixed },
  { value: SalaryTypeV3EnumDto.Commission, to: new Date('2018-12-31') },
  { value: SalaryTypeV3EnumDto.Honorarium, to: new Date('2018-12-31') },
  { value: SalaryTypeV3EnumDto.StraightPieceWork, to: new Date('2018-12-31') },
];

const appointmentTypeTimeline: readonly EnumTimelineItem<AppointmentTypeV3EnumDto>[] = [
  { value: AppointmentTypeV3EnumDto.Temporary },
  { value: AppointmentTypeV3EnumDto.Permanent },
  { value: AppointmentTypeV3EnumDto.PermanentHiredOut, from: new Date('2025-01-01') },
  { value: AppointmentTypeV3EnumDto.TemporaryHiredOut, from: new Date('2025-01-01') },
  { value: AppointmentTypeV3EnumDto.TemporaryOnCall, from: new Date('2025-01-01') },
];

function localizeEnumValue(value: string, enumName: EnumTypeName): string {
  const translatorFnName = `get${enumName}` as keyof typeof EnumLocalizationHelper;
  const translatorFn = EnumLocalizationHelper[translatorFnName];

  if (translatorFn && typeof translatorFn === 'function') {
    return (translatorFn as (val: LocalizableEnumType) => string)(value as LocalizableEnumType);
  }

  return String(value);
}

function getFilteredLookupItems<T extends string>(
  timeline: readonly EnumTimelineItem<T>[],
  enumName: EnumTypeName,
  periodFrom: Date | null | undefined,
  periodTo: Date | null | undefined,
): LookupItem<T>[] {
  return timeline
    .filter((item) => isTimelineItemActiveAtAnyPointInPeriod(item, periodFrom, periodTo))
    .map((item) => ({
      id: item.value,
      name: localizeEnumValue(item.value, enumName),
    }));
}

/**
 * Returns salary type enum values filtered by whether their validity period
 * overlaps with the given date range. Localized for use in dropdowns.
 */
export function getSalaryTypeEnumLookupItems(
  periodFrom: Date | null | undefined,
  periodTo: Date | null | undefined,
): LookupItem<SalaryTypeV3EnumDto>[] {
  return getFilteredLookupItems(salaryTypeTimeline, 'SalaryTypeV3EnumDto', periodFrom, periodTo);
}

/**
 * Returns appointment type enum values filtered by whether their validity period
 * overlaps with the given date range. Localized for use in dropdowns.
 */
export function getAppointmentTypeEnumLookupItems(
  periodFrom: Date | null | undefined,
  periodTo: Date | null | undefined,
): LookupItem<AppointmentTypeV3EnumDto>[] {
  return getFilteredLookupItems(
    appointmentTypeTimeline,
    'AppointmentTypeV3EnumDto',
    periodFrom,
    periodTo,
  );
}
